import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import { Link } from "react-router-dom";
import { deleteIndex, listIndexes } from "../api/agentApi";
import { alpha, useTheme } from "@mui/material/styles";
import { BRAND_COLORS, PAGE_SHELL, PAGE_TYPOGRAPHY, UI_RADIUS } from "../constants";
import { useUser } from "./UserContext";
import { PERMISSIONS } from "../permissions";

const ACTIVE_INDEX_STATUSES = new Set(["pending", "queued", "running", "processing"]);
const POLL_INTERVAL_MS = 5000;

const colors = {
  ink: "#101828",
  muted: "#6F6A64",
  border: "#E7D8CA",
  line: "#EFE5DD",
  copper: BRAND_COLORS.primary.main,
  copperDark: BRAND_COLORS.primary.dark,
  copperSoft: BRAND_COLORS.primary.soft,
  panel: "#FFFDFB",
  field: "#F7F1EC",
  green: "#4F7D4A",
  greenSoft: "#EEF7ED",
  blue: "#3D6E9F",
  blueSoft: "#EAF3FA",
  amber: "#A66A21",
  amberSoft: "#FFF6E5",
  red: "#B42318",
  redSoft: "#FDECEC",
};

const statusTone = {
  completed: {
    label: "Ready",
    color: colors.green,
    bg: colors.greenSoft,
    icon: CheckCircleOutlineIcon,
  },
  failed: {
    label: "Needs attention",
    color: colors.red,
    bg: colors.redSoft,
    icon: ErrorOutlineIcon,
  },
  queued: {
    label: "Queued",
    color: colors.amber,
    bg: colors.amberSoft,
    icon: HourglassEmptyIcon,
  },
  pending: {
    label: "Pending",
    color: colors.amber,
    bg: colors.amberSoft,
    icon: HourglassEmptyIcon,
  },
  running: {
    label: "Processing",
    color: colors.blue,
    bg: colors.blueSoft,
    icon: RefreshIcon,
  },
  processing: {
    label: "Processing",
    color: colors.blue,
    bg: colors.blueSoft,
    icon: RefreshIcon,
  },
};

const filters = [
  { id: "all", label: "All" },
  { id: "ready", label: "Ready" },
  { id: "active", label: "Processing" },
  { id: "failed", label: "Needs attention" },
];

const formControlSx = (palette) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: UI_RADIUS.input,
    bgcolor: palette.buttonSurface,
    "& fieldset": { borderColor: palette.border },
    "&:hover fieldset": { borderColor: BRAND_COLORS.primary.border },
    "&.Mui-focused fieldset": { borderColor: palette.copper },
  },
});

const primaryActionButtonSx = {
  bgcolor: "primary.main",
  color: "primary.contrastText",
  borderRadius: UI_RADIUS.button,
  px: 2.6,
  py: 1.15,
  fontSize: "0.95rem",
  fontWeight: 900,
  textTransform: "none",
  boxShadow: `0 10px 24px ${BRAND_COLORS.primary.shadow}`,
  "&:hover": {
    bgcolor: "primary.dark",
    transform: "translateY(-1px)",
    boxShadow: `0 14px 30px ${BRAND_COLORS.primary.shadowHover}`,
  },
};

function normalizeStatus(status) {
  return String(status || "unknown").toLowerCase();
}

function getStatusTone(status, palette = colors) {
  const normalized = normalizeStatus(status);
  if (["completed"].includes(normalized)) {
    return { label: "Ready", color: palette.green, bg: palette.greenSoft, icon: CheckCircleOutlineIcon };
  }
  if (["failed"].includes(normalized)) {
    return { label: "Needs attention", color: palette.red, bg: palette.redSoft, icon: ErrorOutlineIcon };
  }
  if (["queued", "pending"].includes(normalized)) {
    return { label: normalized === "queued" ? "Queued" : "Pending", color: palette.amber, bg: palette.amberSoft, icon: HourglassEmptyIcon };
  }
  if (["running", "processing"].includes(normalized)) {
    return { label: "Processing", color: palette.blue, bg: palette.blueSoft, icon: RefreshIcon };
  }
  return statusTone[normalized] || {
    label: status || "Unknown",
    color: palette.muted,
    bg: palette.panelSoft || "#F1EFED",
    icon: FolderOpenOutlinedIcon,
  };
}

function useKnowledgeColors() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return {
    ink: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
    border: theme.palette.divider,
    line: theme.palette.divider,
    copper: theme.palette.primary.main,
    copperDark: isDark ? "#F2C69B" : "#935C29",
    copperSoft: isDark ? alpha(theme.palette.primary.main, 0.16) : "rgba(184, 115, 51, 0.10)",
    panel: theme.palette.background.paper,
    panelSoft: isDark ? alpha("#FFF7EF", 0.06) : "#F1EFED",
    field: isDark ? alpha("#FFF7EF", 0.08) : "#F7F1EC",
    green: isDark ? "#91CD99" : "#4F7D4A",
    greenSoft: isDark ? alpha("#91CD99", 0.16) : "#EEF7ED",
    blue: isDark ? "#9EC8EF" : "#3D6E9F",
    blueSoft: isDark ? alpha("#9EC8EF", 0.16) : "#EAF3FA",
    amber: isDark ? "#F2C56F" : "#A66A21",
    amberSoft: isDark ? alpha("#F2C56F", 0.16) : "#FFF6E5",
    red: isDark ? "#FF9C91" : "#B42318",
    redSoft: isDark ? alpha("#FF9C91", 0.14) : "#FDECEC",
    buttonSurface: isDark ? alpha("#FFF7EF", 0.06) : "#fff",
    shadow: isDark ? "0 18px 42px rgba(0,0,0,0.34)" : "0 6px 18px rgba(40, 28, 18, 0.04)",
  };
}

function getSourceCount(index) {
  const sources = Array.isArray(index?.source_path) ? index.source_path : [];
  return sources.length;
}

function clampPercent(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(100, numeric));
}

function pluralizeFile(count) {
  return Number(count) === 1 ? "file" : "files";
}

function getProgressSummary(index) {
  const progress = index?.progress || {};
  const status = normalizeStatus(index?.status);
  const total = Number(progress.total || 0);
  const remaining = Number(progress.remaining || 0);
  const failed = Number(progress.failed || 0);
  const completed = Number(progress.completed || 0);
  const skipped = Number(progress.skipped || 0);
  const percent = clampPercent(progress.percent);

  if (progress.label) {
    return {
      total,
      remaining,
      failed,
      percent,
      label: progress.label,
      determinate: total > 0,
    };
  }

  if (ACTIVE_INDEX_STATUSES.has(status)) {
    return {
      total,
      remaining,
      failed,
      percent,
      label: total > 0
        ? `${percent}% complete · ${remaining} ${pluralizeFile(remaining)} remaining`
        : "Preparing files...",
      determinate: total > 0,
    };
  }

  if (status === "completed") {
    return {
      total,
      remaining: 0,
      failed,
      percent: 100,
      label: total > 0 ? `100% complete · ${total} ${pluralizeFile(total)} indexed` : "Ready",
      determinate: true,
    };
  }

  if (status === "failed") {
    const processed = completed + skipped + failed;
    return {
      total,
      remaining,
      failed,
      percent,
      label: total > 0 ? `${failed} ${pluralizeFile(failed)} failed · ${processed} of ${total} processed` : "Indexing failed",
      determinate: total > 0,
    };
  }

  return {
    total,
    remaining,
    failed,
    percent,
    label: index?.message || "",
    determinate: total > 0,
  };
}

function formatDate(value) {
  if (!value) return "Not updated yet";
  try {
    return new Date(value).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "Not updated yet";
  }
}

function statusBucket(status) {
  const normalized = normalizeStatus(status);
  if (normalized === "completed") return "ready";
  if (normalized === "failed") return "failed";
  if (ACTIVE_INDEX_STATUSES.has(normalized)) return "active";
  return "all";
}

function SummaryCard({ icon: Icon, label, value, tone = colors.copper, subtext }) {
  const colors = useKnowledgeColors();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        borderRadius: UI_RADIUS.card,
        border: `1px solid ${colors.border}`,
        bgcolor: colors.panel,
        minHeight: 132,
        transition: "all 180ms ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: colors.shadow,
          borderColor: "rgba(184, 115, 51, 0.38)",
        },
      }}
    >
      <Stack spacing={1.4}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: UI_RADIUS.panel,
            bgcolor: `${tone}16`,
            color: tone,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Typography variant="body2" sx={{ color: colors.muted, fontWeight: 800 }}>
          {label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 900, color: colors.ink, lineHeight: 1 }}>
          {value}
        </Typography>
        {subtext && (
          <Typography variant="body2" sx={{ color: colors.muted }}>
            {subtext}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

function KnowledgeSpaceCard({ index, deleting, onDelete, canDelete }) {
  const colors = useKnowledgeColors();
  const tone = getStatusTone(index.status, colors);
  const StatusIcon = tone.icon;
  const active = ACTIVE_INDEX_STATUSES.has(normalizeStatus(index.status));
  const progress = getProgressSummary(index);
  const description = index.description || "No description yet.";

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.4,
        borderRadius: UI_RADIUS.card,
        border: `1px solid ${colors.border}`,
        bgcolor: colors.panel,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow: "0 6px 18px rgba(40, 28, 18, 0.04)",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 18px 34px rgba(40, 28, 18, 0.10)",
          borderColor: "rgba(184, 115, 51, 0.50)",
        },
      }}
    >
      <Stack direction="row" spacing={1.6} alignItems="flex-start">
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: UI_RADIUS.panel,
            bgcolor: colors.copperSoft,
            color: colors.copperDark,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <DescriptionOutlinedIcon />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.8, flexWrap: "wrap" }}>
            <Chip
              size="small"
              icon={<StatusIcon />}
              label={tone.label}
              sx={{
                bgcolor: tone.bg,
                color: tone.color,
                border: `1px solid ${tone.color}33`,
                borderRadius: UI_RADIUS.chip,
                fontWeight: 800,
                "& .MuiChip-icon": { color: tone.color },
              }}
            />
            <Chip
              size="small"
              label={index.type === "code" ? "Code" : "Documents"}
              sx={{
                bgcolor: colors.buttonSurface,
                border: `1px solid ${colors.border}`,
                color: colors.muted,
                borderRadius: UI_RADIUS.chip,
                fontWeight: 700,
              }}
            />
          </Stack>
          <Typography
            variant="h6"
            sx={{
              color: colors.ink,
              fontWeight: 900,
              lineHeight: 1.16,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {index.name || "Untitled space"}
          </Typography>
        </Box>
      </Stack>

      <Typography
        variant="body2"
        sx={{
          color: colors.muted,
          lineHeight: 1.55,
          minHeight: 44,
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {description}
      </Typography>

      {active && (
        <Box>
          <LinearProgress
            variant={progress.determinate ? "determinate" : "indeterminate"}
            value={progress.percent}
            sx={{
              height: 6,
              borderRadius: 99,
              bgcolor: colors.line,
              "& .MuiLinearProgress-bar": {
                bgcolor: colors.copper,
                borderRadius: 99,
              },
            }}
          />
          <Typography variant="caption" sx={{ color: colors.muted, mt: 0.7, display: "block" }}>
            {progress.label || "Processing updates automatically."}
          </Typography>
        </Box>
      )}

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          pt: 0.4,
          mt: "auto",
          color: colors.muted,
        }}
      >
        <FolderOpenOutlinedIcon sx={{ fontSize: 18, color: colors.copper }} />
        <Typography variant="body2" sx={{ flex: 1 }}>
          {getSourceCount(index)} source{getSourceCount(index) === 1 ? "" : "s"}
        </Typography>
        <Typography variant="body2">
          {formatDate(index.updated_at || index.created_at)}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          component={Link}
          to={`/kb/${index.type}/${index.index_id}`}
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          sx={{
            bgcolor: colors.copper,
            color: "#fff",
            borderRadius: UI_RADIUS.button,
            px: 2.2,
            fontWeight: 900,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { bgcolor: colors.copperDark, boxShadow: "0 10px 24px rgba(184, 115, 51, 0.22)" },
          }}
        >
          Open
        </Button>
        <Box sx={{ flex: 1 }} />
        {canDelete && (
          <Tooltip title="Delete knowledge space">
            <span>
              <IconButton
                onClick={() => onDelete(index.index_id, index.name)}
                disabled={deleting === index.index_id}
                sx={{
                  color: colors.muted,
                  border: `1px solid ${colors.border}`,
                  bgcolor: colors.buttonSurface,
                  borderRadius: UI_RADIUS.iconButton,
                  "&:hover": {
                    color: colors.red,
                    borderColor: "#F2B8B5",
                    bgcolor: colors.redSoft,
                  },
                }}
              >
                {deleting === index.index_id ? <CircularProgress size={18} /> : <DeleteOutlineIcon />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
    </Paper>
  );
}

function KnowledgeSpacesOverview({ indexType, canDelete }) {
  const colors = useKnowledgeColors();
  const [indexes, setIndexes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deleting, setDeleting] = useState("");
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const refreshIndexes = async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    if (!showSpinner) setRefreshing(true);
    try {
      const data = await listIndexes(indexType);
      setIndexes(data.indexes || []);
      setError("");
    } catch {
      setError("Failed to fetch knowledge spaces.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    let poller;

    const poll = async (showSpinner = false) => {
      if (showSpinner) setLoading(true);
      try {
        const data = await listIndexes(indexType);
        if (cancelled) return;
        const nextIndexes = data.indexes || [];
        setIndexes(nextIndexes);
        setError("");
        setLoading(false);
        if (nextIndexes.some(index => ACTIVE_INDEX_STATUSES.has(normalizeStatus(index.status)))) {
          poller = setTimeout(() => poll(false), POLL_INTERVAL_MS);
        }
      } catch {
        if (cancelled) return;
        setError("Failed to fetch knowledge spaces.");
        setLoading(false);
      }
    };

    poll(true);
    return () => {
      cancelled = true;
      if (poller) clearTimeout(poller);
    };
  }, [indexType]);

  const handleDelete = async (indexId, indexName) => {
    if (!canDelete) return;
    if (!window.confirm(`Delete knowledge space "${indexName || indexId}"? This cannot be undone.`)) return;
    setDeleting(indexId);
    setError("");
    try {
      await deleteIndex(indexId);
      setIndexes(currentIndexes => currentIndexes.filter(idx => idx.index_id !== indexId));
    } catch (e) {
      let detail = "Failed to delete knowledge space.";
      if (e?.message) {
        try {
          const parsed = JSON.parse(e.message);
          detail = typeof parsed?.detail === "string" ? parsed.detail : e.message;
        } catch {
          detail = e.message;
        }
      } else if (typeof e?.detail === "string") {
        detail = e.detail;
      }
      setError(detail);
    } finally {
      setDeleting("");
    }
  };

  const summary = useMemo(() => {
    const total = indexes.length;
    const ready = indexes.filter(index => normalizeStatus(index.status) === "completed").length;
    const active = indexes.filter(index => ACTIVE_INDEX_STATUSES.has(normalizeStatus(index.status))).length;
    const failed = indexes.filter(index => normalizeStatus(index.status) === "failed").length;
    return { total, ready, active, failed };
  }, [indexes]);

  const filteredIndexes = useMemo(() => {
    const search = query.trim().toLowerCase();
    return indexes.filter(index => {
      const bucket = statusBucket(index.status);
      if (filter !== "all" && bucket !== filter) return false;
      if (!search) return true;
      return [index.name, index.description, index.index_id]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(search));
    });
  }, [indexes, query, filter]);

  return (
    <Stack spacing={3}>
      {error && <Alert severity="error">{error}</Alert>}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(4, minmax(0, 1fr))",
          },
          gap: PAGE_SHELL.sectionGap,
        }}
      >
        <SummaryCard icon={FolderOpenOutlinedIcon} label="Spaces" value={summary.total} subtext="Total collections" />
        <SummaryCard icon={CheckCircleOutlineIcon} label="Ready" value={summary.ready} tone={colors.green} subtext="Available to agents" />
        <SummaryCard icon={HourglassEmptyIcon} label="Processing" value={summary.active} tone={colors.amber} subtext="Queued or indexing" />
        <SummaryCard icon={ErrorOutlineIcon} label="Needs attention" value={summary.failed} tone={colors.red} subtext="Failed jobs" />
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          borderRadius: UI_RADIUS.panel,
          border: `1px solid ${colors.border}`,
          bgcolor: colors.panel,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
        >
          <TextField
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Search spaces"
            size="small"
            sx={{ flex: 1, ...formControlSx(colors) }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.muted }} />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
            {filters.map(item => (
              <Chip
                key={item.id}
                label={item.label}
                onClick={() => setFilter(item.id)}
                sx={{
                  border: `1px solid ${filter === item.id ? colors.copper : colors.border}`,
                  bgcolor: filter === item.id ? colors.copperSoft : colors.buttonSurface,
                  color: filter === item.id ? colors.copperDark : colors.muted,
                  borderRadius: UI_RADIUS.chip,
                  fontWeight: 800,
                  "&:hover": { bgcolor: colors.copperSoft },
                }}
              />
            ))}
          </Stack>
          <Tooltip title="Refresh spaces">
            <span>
              <IconButton
                onClick={() => refreshIndexes(false)}
                disabled={loading || refreshing}
                sx={{
                  color: colors.copperDark,
                  border: `1px solid ${colors.border}`,
                  bgcolor: colors.buttonSurface,
                  borderRadius: UI_RADIUS.iconButton,
                  "&:hover": { bgcolor: colors.copperSoft },
                }}
              >
                {refreshing ? <CircularProgress size={18} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      {loading ? (
        <Paper
          elevation={0}
          sx={{
            p: 5,
            borderRadius: UI_RADIUS.panel,
            border: `1px solid ${colors.border}`,
            textAlign: "center",
            color: colors.muted,
          }}
        >
          <CircularProgress sx={{ color: colors.copper }} />
          <Typography sx={{ mt: 2, fontWeight: 700 }}>Loading knowledge spaces...</Typography>
        </Paper>
      ) : filteredIndexes.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 5,
            borderRadius: UI_RADIUS.panel,
            border: `1px solid ${colors.border}`,
            textAlign: "center",
            bgcolor: colors.panel,
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 42, color: colors.copper, mb: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 900, color: colors.ink }}>
            No matching spaces
          </Typography>
          <Typography sx={{ color: colors.muted, mt: 0.6 }}>
            Create a knowledge space or adjust the current filters.
          </Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
              xl: "repeat(3, minmax(0, 1fr))",
            },
            gap: PAGE_SHELL.sectionGap,
          }}
        >
          {filteredIndexes.map(index => (
            <KnowledgeSpaceCard
              key={index.index_id}
              index={index}
              deleting={deleting}
              onDelete={handleDelete}
              canDelete={canDelete}
            />
          ))}
        </Box>
      )}
    </Stack>
  );
}

export default function KnowledgeBuilderListPage() {
  const colors = useKnowledgeColors();
  const { can } = useUser();
  const canCreateKnowledge = Boolean(can?.(PERMISSIONS.KNOWLEDGE_CREATE));
  const canDeleteKnowledge = Boolean(can?.(PERMISSIONS.KNOWLEDGE_DELETE));
  return (
    <Box sx={{ px: PAGE_SHELL.paddingX, py: PAGE_SHELL.paddingY, maxWidth: PAGE_SHELL.maxWidth, mx: "auto" }}>
      <Stack spacing={1.9} sx={{ mb: PAGE_SHELL.headerMarginBottom }}>
        <Box>
          <Typography variant="h3" component="h1" sx={PAGE_TYPOGRAPHY.heading}>
            Knowledge Spaces
          </Typography>
          <Typography variant="body2" sx={{ color: colors.muted, mt: 0.35 }}>
            Upload documents, monitor processing, and attach trusted knowledge to your agents.
          </Typography>
        </Box>
        {canCreateKnowledge && (
          <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap flexWrap="wrap" sx={{ pt: 0.2 }}>
            <Button
              component={Link}
              to="/kb/document-indexer"
              variant="contained"
              startIcon={<AddIcon />}
              sx={primaryActionButtonSx}
            >
              Create Knowledge Space
            </Button>
          </Stack>
        )}
      </Stack>

      <KnowledgeSpacesOverview indexType="doc" canDelete={canDeleteKnowledge} />
    </Box>
  );
}
